/**
 * FitBangla Header JavaScript
 * Handles:
 * 1. Scroll-driven sticky pill transition with color inversion
 * 2. Preserving sticky state during cart drawer & modal open
 * 3. Smooth animated account popover with robust relative positioning
 * 4. Country / currency dropdown toggle
 * 5. Mega-menu product showcase carousel scroll
 * 6. Mobile drawer interactions
 * 7. Live cart count synchronization
 */

(function () {
  'use strict';

  function initFitBanglaHeader() {
    const headerWrapper = document.querySelector('[data-fitbangla-header-wrapper]');
    const header = document.querySelector('[data-fitbangla-header]');
    if (!header) return;

    const isTransparentEnabled = header.classList.contains('fb-header--transparent');

    // 1. Scroll-based Sticky Floating Pill Transition
    let lastScrollY = window.scrollY;
    let ticking = false;

    function handleScroll() {
      // If a modal or drawer is open (such as cart drawer or search modal), preserve sticky state
      if (document.documentElement.hasAttribute('scroll-lock') || document.querySelector('dialog[open]')) {
        ticking = false;
        return;
      }

      const scrollY = window.scrollY;

      if (scrollY > 35) {
        if (!header.classList.contains('is-sticky')) {
          header.classList.add('is-sticky');
        }
      } else {
        if (header.classList.contains('is-sticky')) {
          header.classList.remove('is-sticky');
        }
      }

      lastScrollY = scrollY;
      ticking = false;
    }

    window.addEventListener('scroll', function () {
      if (!ticking) {
        window.requestAnimationFrame(handleScroll);
        ticking = true;
      }
    }, { passive: true });

    // Initial check
    handleScroll();

    // 2. Header Active State Synchronization (for popovers/dropdowns)
    function setHeaderActive(active) {
      if (isTransparentEnabled && !header.classList.contains('is-sticky')) {
        if (active) {
          header.classList.add('is-header-active');
        } else {
          const isAccountOpen = document.querySelector('.fb-account-popover.is-open');
          const isCountryOpen = document.querySelector('.fb-country-dropdown.is-open');
          if (!isAccountOpen && !isCountryOpen) {
            header.classList.remove('is-header-active');
          }
        }
      }
    }

    // 3. Dynamic Country / Currency Dropdown
    const countryWrap = header.querySelector('[data-country-wrap]');
    const countryToggle = header.querySelector('[data-country-toggle]');
    const countryDropdown = header.querySelector('#fb-country-dropdown-list');

    if (countryToggle && countryDropdown) {
      function openCountryDropdown() {
        countryDropdown.classList.add('is-open');
        countryToggle.setAttribute('aria-expanded', 'true');
        setHeaderActive(true);
      }

      function closeCountryDropdown() {
        if (!countryDropdown.classList.contains('is-open')) return;
        countryDropdown.classList.remove('is-open');
        countryToggle.setAttribute('aria-expanded', 'false');
        setHeaderActive(false);
      }

      countryToggle.addEventListener('click', (e) => {
        e.stopPropagation();
        const isOpen = countryDropdown.classList.contains('is-open');
        if (!isOpen) {
          openCountryDropdown();
        } else {
          closeCountryDropdown();
        }
      });

      if (countryWrap) {
        countryWrap.addEventListener('mouseenter', () => {
          openCountryDropdown();
        });
        countryWrap.addEventListener('mouseleave', () => {
          closeCountryDropdown();
        });
      }

      // Clean up any legacy mock currency values from localStorage
      try {
        localStorage.removeItem('fb_selected_currency');
        localStorage.removeItem('fb_selected_flag');
        localStorage.removeItem('fb_selected_symbol');
      } catch (err) {}

      // Handle dynamic country selection and submit Shopify localization form
      const countryButtons = countryDropdown.querySelectorAll('.fb-country-dropdown__btn');
      const codeInput = header.querySelector('#fb-country-code-input');
      const locForm = header.querySelector('#fb-localization-form');

      countryButtons.forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const val = btn.getAttribute('value');
          if (codeInput && val) {
            codeInput.value = val;
          }
          closeCountryDropdown();
          if (locForm) {
            locForm.submit();
          }
        });
      });

      document.addEventListener('click', (e) => {
        if (!countryToggle.contains(e.target) && !countryDropdown.contains(e.target)) {
          closeCountryDropdown();
        }
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          closeCountryDropdown();
        }
      });
    }

    // 4. Smooth Account Popover (Directly Positioned Under Button)
    const accountTrigger = header.querySelector('[data-account-trigger]');
    const accountPopover = header.querySelector('[data-account-popover]');

    if (accountTrigger && accountPopover) {
      accountTrigger.addEventListener('click', (e) => {
        e.stopPropagation();
        const isOpen = accountPopover.classList.contains('is-open');

        if (!isOpen) {
          accountPopover.hidden = false;
          // Force layout reflow so animation kicks in
          void accountPopover.offsetHeight;
          accountPopover.classList.add('is-open');
          accountTrigger.setAttribute('aria-expanded', 'true');
          setHeaderActive(true);
        } else {
          closeAccountPopover();
        }
      });

      function closeAccountPopover() {
        if (!accountPopover.classList.contains('is-open')) return;
        accountPopover.classList.remove('is-open');
        accountTrigger.setAttribute('aria-expanded', 'false');

        setTimeout(() => {
          if (!accountPopover.classList.contains('is-open')) {
            accountPopover.hidden = true;
          }
        }, 280);

        setHeaderActive(false);
      }

      document.addEventListener('click', (e) => {
        if (!accountTrigger.contains(e.target) && !accountPopover.contains(e.target)) {
          closeAccountPopover();
        }
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          closeAccountPopover();
        }
      });
    }

    // 5. Cart Drawer Trigger (Preserve Header Sticky State)
    const cartTrigger = header.querySelector('[data-cart-trigger]');
    if (cartTrigger) {
      cartTrigger.addEventListener('click', (e) => {
        e.preventDefault();
        // Ensure header stays in white pill mode while cart is visible
        header.classList.add('is-sticky');

        const drawer = document.querySelector('cart-drawer-component');
        if (drawer && typeof drawer.open === 'function') {
          drawer.open();
        } else {
          window.location.assign('/cart');
        }
      });
    }

    // When cart drawer or dialog closes, recheck scroll position
    document.addEventListener('dialog:close', () => {
      setTimeout(() => {
        if (!document.documentElement.hasAttribute('scroll-lock') && window.scrollY <= 35) {
          header.classList.remove('is-sticky');
        }
      }, 150);
    });

    // 6. Product Showcase Prev/Next Arrows
    const headerSection = header.closest('.shopify-section') || document;
    const showcaseTracks = headerSection.querySelectorAll('[data-showcase-track]');
    showcaseTracks.forEach(track => {
      const parent = track.closest('.fb-mega__showcase');
      if (!parent) return;

      const prevBtn = parent.querySelector('[data-showcase-prev]');
      const nextBtn = parent.querySelector('[data-showcase-next]');

      if (prevBtn) {
        prevBtn.addEventListener('click', (e) => {
          e.preventDefault();
          track.scrollBy({ left: -220, behavior: 'smooth' });
        });
      }

      if (nextBtn) {
        nextBtn.addEventListener('click', (e) => {
          e.preventDefault();
          track.scrollBy({ left: 220, behavior: 'smooth' });
        });
      }
    });

    // 7. Mobile Drawer Toggle
    const mobileToggle = header.querySelector('[data-mobile-menu-toggle]');
    const mobileDrawer = document.querySelector('[data-mobile-drawer]');
    const drawerCloses = document.querySelectorAll('[data-mobile-drawer-close]');

    if (mobileToggle && mobileDrawer) {
      let previousOverflow = '';
      const closeMobileDrawer = () => {
        if (!mobileDrawer.classList.contains('is-open')) return;
        mobileDrawer.classList.remove('is-open');
        mobileDrawer.setAttribute('aria-hidden', 'true');
        mobileToggle.setAttribute('aria-expanded', 'false');
        document.body.style.overflow = previousOverflow;
        mobileToggle.focus();
      };

      mobileToggle.addEventListener('click', () => {
        previousOverflow = document.body.style.overflow;
        mobileDrawer.classList.add('is-open');
        mobileDrawer.setAttribute('aria-hidden', 'false');
        mobileToggle.setAttribute('aria-expanded', 'true');
        document.body.style.overflow = 'hidden';
        mobileDrawer.querySelector('button[data-mobile-drawer-close]')?.focus();
      });

      drawerCloses.forEach(button => button.addEventListener('click', closeMobileDrawer));
      mobileDrawer.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          closeMobileDrawer();
        }
        if (event.key === 'Tab') {
          const focusable = Array.from(mobileDrawer.querySelectorAll('a[href], button, summary'))
            .filter(element => element.getClientRects().length > 0 && !element.disabled);
          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last?.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first?.focus();
          }
        }
      });
      window.matchMedia('(max-width: 1024px)').addEventListener('change', (event) => {
        if (!event.matches) closeMobileDrawer();
      });
    }

    // 8. Real-time Cart Count Synchronization
    document.addEventListener('cart:updated', function (e) {
      const count = e.detail && e.detail.item_count !== undefined ? e.detail.item_count : null;
      updateCartBadge(count);
    });

    const originalFetch = window.fetch;
    if (originalFetch) {
      window.fetch = async function (...args) {
        const response = await originalFetch.apply(this, args);
        try {
          const url = args[0] ? (typeof args[0] === 'string' ? args[0] : args[0].url) : '';
          if (url && (url.includes('/cart/add') || url.includes('/cart/change') || url.includes('/cart/update') || url.includes('/cart/clear'))) {
            fetch('/cart.js')
              .then(res => res.json())
              .then(cart => {
                updateCartBadge(cart.item_count);
              })
              .catch(() => {});
          }
        } catch (err) {}
        return response;
      };
    }

    function updateCartBadge(count) {
      if (count === null || count === undefined) return;
      const bubbles = document.querySelectorAll('[data-cart-count]');
      bubbles.forEach(bubble => {
        bubble.textContent = count;
        bubble.style.display = count > 0 ? 'inline-flex' : 'none';
      });
    }

    // 9. Delegated Cart-Drawer Upsell AJAX Add-To-Cart
    document.addEventListener('click', function (e) {
      const btn = e.target.closest('[data-upsell-add-btn]');
      if (!btn) return;
      e.preventDefault();
      const variantId = btn.getAttribute('data-variant-id');
      if (variantId && typeof window.fitbAddUpsellToCart === 'function') {
        window.fitbAddUpsellToCart(variantId, btn);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFitBanglaHeader);
  } else {
    initFitBanglaHeader();
  }

  document.addEventListener('shopify:section:load', initFitBanglaHeader);
})();
